import { get, patch, post } from "utilities/network";

export type LifecycleStatus = "draft" | "scheduled" | "active" | "archived";
export type RewardApplication = "auto" | "manual";
export type RewardValueShape = "fixed" | "percentage" | "perk";
export type TriggerKind = "tier" | "transaction" | "event";
export type ActionCategory =
  | "revenue_generating"
  | "revenue_critical"
  | "trust_building"
  | "stickiness"
  | "one_time_milestone";

export type ActionCategoryOption = {
  code: ActionCategory;
  name: string;
  description: string;
  qualifiesForStreak: boolean;
};

export type GamificationSelectOption = {
  code: string | null;
  name: string;
};

export type GamificationConfigurationOptions = {
  actionCategories: ActionCategoryOption[];
  markets: GamificationSelectOption[];
  currencies: Array<GamificationSelectOption & { code: string }>;
  limitBuckets: Array<GamificationSelectOption & { code: string }>;
};

export type GamificationAction = {
  id: number;
  code: string;
  name: string;
  actionKind: TriggerKind;
  transactionFamily: string | null;
  eventKey: string | null;
  tier: 1 | 2 | 3 | null;
  eloCategory: string;
  status: LifecycleStatus;
  eloMapping: {
    id: number;
    eloPoints: number;
    minValue: number | string | null;
    mappingStatus: LifecycleStatus;
  } | null;
};

export type EloTierPerk = {
  id?: number;
  tierId?: number;
  code: string;
  title: string;
  description: string;
  sortOrder: number;
};

export type EloTier = {
  id: number;
  code: string;
  name: string;
  lowerLimit: number;
  upperLimit: number;
  maintenanceElo?: number;
  freezeDays?: number;
  graceDays?: number;
  memberCount?: number;
  status: LifecycleStatus;
  sortOrder: number;
  version?: number;
  market?: string | null;
  effectiveFrom?: string;
  effectiveTo?: string | null;
  perks?: EloTierPerk[];
  rewardDefinitionId?: number | null;
  rewardDefinitionIds?: number[];
};

export type EloTierInput = {
  code?: string;
  name: string;
  lowerLimit: number;
  upperLimit: number;
  maintenanceElo?: number;
  freezeDays?: number;
  graceDays?: number;
  status?: LifecycleStatus;
  sortOrder?: number;
  market?: string | null;
  effectiveFrom?: string;
  effectiveTo?: string | null;
  perks?: Array<Omit<EloTierPerk, "id" | "tierId">>;
  rewardDefinitionId?: number | null;
  rewardDefinitionIds?: number[];
  reason?: string;
};

export type StreakPolicy = {
  id?: number;
  configurationKey?: string;
  version?: number;
  market?: string | null;
  lifecycleStatus?: LifecycleStatus;
  effectiveFrom?: string;
  effectiveTo?: string | null;
  windowMilliseconds?: number;
  windowHours?: number;
  standingAtRiskWindowMilliseconds?: number;
  standingAtRiskHours?: number;
  decayPoints: number;
  tierMultipliers: Record<string, number>;
};

export type StreakPolicyInput = {
  market?: string | null;
  status?: LifecycleStatus;
  effectiveFrom: string;
  effectiveTo?: string | null;
  windowHours: number;
  standingAtRiskHours: number;
  decayPoints: number;
  tierMultipliers: Record<string, number>;
  reason: string;
};

export type ShieldAllowance = {
  id: number;
  effectiveMonth: string;
  allowance: number;
  updatedBy: number;
};

export type ShieldAllowanceView = {
  active: ShieldAllowance | null;
  pending: ShieldAllowance | null;
};

export type StreakEvent = {
  id: string;
  userId: number | null;
  eventType: string;
  actorType: string;
  actorId: number | null;
  sourceType: string | null;
  sourceId: string | null;
  beforeState: Record<string, unknown> | null;
  afterState: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  occurredAt: string;
};

export type PaginationMeta = {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalPages: number;
  currentPage: number;
  nextPage?: number | null;
};

export type RewardType = {
  code: string;
  name: string;
  shape: RewardValueShape;
};

export type RewardDefinition = {
  id: number;
  configurationKey: string;
  version: number;
  name: string;
  rewardType: string;
  application: RewardApplication;
  status: LifecycleStatus;
  effectiveFrom: string;
  effectiveTo: string | null;
  market: string | null;
  currency: string | null;
  fixedAmount: number | string | null;
  percentageRate: number | string | null;
  floorAmount: number | string | null;
  ceilingAmount: number | string | null;
  maxUses: number;
  expiresAt: string | null;
  limitBuckets: string[] | null;
  perkCode: string | null;
  perkNotes: string | null;
};

export type RewardDefinitionInput = {
  name: string;
  rewardType: string;
  application: RewardApplication;
  status: LifecycleStatus;
  effectiveFrom: string;
  effectiveTo?: string | null;
  market?: string | null;
  currency?: string | null;
  fixedAmount?: number | null;
  percentageRate?: number | null;
  floorAmount?: number | null;
  ceilingAmount?: number | null;
  quantity: number;
  expiresAt?: string | null;
  limitBuckets?: string[] | null;
  perkCode?: string | null;
  perkNotes?: string | null;
  reason?: string;
};

export type StreakMilestone = {
  id: number;
  name: string;
  streakCount: number;
  weeks?: number;
  eloPoints: number;
  rewardDefinitionId: number | null;
  rewardDefinitionIds?: number[];
  status: LifecycleStatus;
};

export type StreakMilestoneInput = {
  name: string;
  streakCount: number;
  eloPoints: number;
  rewardDefinitionId?: number | null;
  rewardDefinitionIds?: number[];
  status: LifecycleStatus;
  reason?: string;
};

export type RewardTrigger = {
  kind: TriggerKind;
  tier?: number;
  family?: string;
  count?: number;
  minValue?: number | null;
};

export type RewardMapping = {
  id: number;
  configurationKey?: string | null;
  version: number;
  effectiveFrom?: string;
  effectiveTo?: string | null;
  market?: string | null;
  mappingStatus: LifecycleStatus;
  triggerKind: TriggerKind;
  triggerFingerprint: string;
  triggerConfig: RewardTrigger;
  rewardName: string;
  rewardType: string;
  application: RewardApplication;
  rewardStatus: LifecycleStatus;
  effectiveDate: string;
  currency?: string | null;
  fixedAmount?: number | string | null;
  percentageRate?: number | string | null;
  floorAmount?: number | string | null;
  ceilingAmount?: number | string | null;
  maxUses?: number | null;
  expiresAt?: string | null;
  limitBuckets?: string[] | null;
  perkCode?: string | null;
  perkNotes?: string | null;
};

export type RewardMappingInput = {
  rewardName: string;
  rewardType: string;
  application: RewardApplication;
  effectiveDate: string;
  effectiveTo?: string | null;
  market?: string | null;
  mappingStatus?: LifecycleStatus;
  rewardStatus?: LifecycleStatus;
  trigger: RewardTrigger;
  currency?: string | null;
  fixedAmount?: number | null;
  percentageRate?: number | null;
  floorAmount?: number | null;
  ceilingAmount?: number | null;
  maxUses?: number | null;
  expiresAt?: string | null;
  limitBuckets?: string[] | null;
  perkCode?: string | null;
  perkNotes?: string | null;
  reason?: string;
};

export type FulfilmentTask = {
  id: number;
  grantId: number;
  status: "pending" | "in_progress" | "completed" | "cancelled";
  assignedAdminId: number | null;
  dueAt: string | null;
  completedAt: string | null;
  completionNote: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
};

export type ConfigurationVersion = {
  id: number;
  entityType: string;
  entityId: number;
  configurationKey: string;
  version: number;
  lifecycleStatus: LifecycleStatus;
  effectiveFrom: string;
  effectiveTo: string | null;
  market: string | null;
  snapshot: Record<string, unknown>;
  changedByAdminId: number | null;
  changeReason: string | null;
  createdAt: string;
  updatedAt: string;
};

export type GamificationDashboardAnalytics = {
  activeEloMembers: number;
  activeStreaks: number;
  grantsIssued: number;
  pendingFulfilment: number;
  overdueFulfilment: number;
  awardsByCategory: Record<string, number>;
  tierDistribution: {
    tiers: Array<{ id: number; code: string; name: string; members: number }>;
    withoutTierMembers: number;
    frozenMembers: number;
    graceMembers: number;
  };
  streakHealth: {
    active: number;
    atRisk: number;
    shieldsUsedThisMonth: number;
  };
  elo: {
    pointsAwarded: number;
  };
  rewardHealth: {
    appliedGrants: number;
    usesConsumed: number;
  };
};

type MutationEnvelope<T> = {
  data?: T;
};

const root = "admin/gamification";

export const mutationData = <T>(response: unknown): T => {
  const value = response as
    | MutationEnvelope<T>
    | { data?: MutationEnvelope<T> }
    | undefined;
  if (value?.data && "data" in (value.data as object)) {
    return (value.data as MutationEnvelope<T>).data as T;
  }
  return (value?.data ?? value) as T;
};

const queryString = (values: Record<string, unknown>) => {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  });
  const result = params.toString();
  return result ? `?${result}` : "";
};

export const gamificationApi = {
  getDashboard: async () =>
    (await get(`${root}/dashboard`)) as {
      analytics: GamificationDashboardAnalytics;
    },
  getConfigurationOptions: async () =>
    (await get(`${root}/configuration-options`)) as GamificationConfigurationOptions,

  listActions: async () =>
    (await get(`${root}/actions`)) as { actions: GamificationAction[] },

  updateAction: async (
    actionId: number,
    input: { category: ActionCategory; eloPoints: number; reason?: string },
  ) =>
    mutationData<{ action: GamificationAction }>(
      await patch(`${root}/actions/${actionId}`, input),
    ),

  createEloMapping: async (input: {
    actionCode: string;
    effectiveDate: string;
    effectiveTo?: string | null;
    market?: string | null;
    eloPoints: number;
    minValue?: number | null;
    mappingStatus?: LifecycleStatus;
    reason?: string;
  }) =>
    mutationData<{ mapping: unknown }>(
      await post(`${root}/elo-mappings/create`, input),
    ),

  listTiers: async () =>
    (await get(`${root}/elo-tiers`)) as { tiers: EloTier[] },

  createTier: async (input: EloTierInput & { code: string }) =>
    mutationData<{ tier: EloTier }>(
      await post(`${root}/elo-tiers/create`, input),
    ),

  updateTier: async (tierId: number, input: EloTierInput) =>
    mutationData<{ tier: EloTier }>(
      await patch(`${root}/elo-tiers/${tierId}`, input),
    ),

  listMilestones: async () => {
    const response = (await get(`${root}/streak-milestones`)) as {
      milestones: Array<Omit<StreakMilestone, "streakCount"> & { weeks: number }>;
    };
    return {
      milestones: response.milestones.map((milestone) => ({
        ...milestone,
        streakCount: milestone.weeks,
      })),
    };
  },

  createMilestone: async (input: StreakMilestoneInput) =>
    mutationData<{ milestone: StreakMilestone }>(
      await post(`${root}/streak-milestones`, input),
    ),

  updateMilestone: async (
    milestoneId: number,
    input: StreakMilestoneInput,
  ) =>
    mutationData<{ milestone: StreakMilestone }>(
      await patch(`${root}/streak-milestones/${milestoneId}`, input),
    ),

  getStreakPolicy: async () =>
    (await get(`${root}/streak-policy`)) as {
      policy: StreakPolicy | null;
    },

  createStreakPolicy: async (input: StreakPolicyInput) =>
    mutationData<{ policy: StreakPolicy }>(
      await post(`${root}/streak-policy/versions`, input),
    ),

  getShieldAllowance: async () =>
    (await get(`${root}/streak-shield-allowance`)) as {
      allowance: ShieldAllowanceView;
    },

  updateShieldAllowance: async (input: {
    allowance: number;
    reason?: string;
  }) =>
    mutationData<{ allowance: ShieldAllowanceView }>(
      await patch(`${root}/streak-shield-allowance`, input),
    ),

  listStreakEvents: async (filters: {
    page?: number;
    limit?: number;
    userId?: number;
    eventType?: string;
    sourceType?: string;
    startDate?: string;
    endDate?: string;
  }) =>
    (await get(
      `${root}/streak-events${queryString(filters)}`,
    )) as {
      results: StreakEvent[];
      meta: PaginationMeta;
    },

  listRewardTypes: async () =>
    (await get(`${root}/reward-types`)) as { rewardTypes: RewardType[] },

  listRewardDefinitions: async () =>
    (await get(`${root}/reward-definitions`)) as {
      rewards: RewardDefinition[];
    },

  createRewardDefinition: async (input: RewardDefinitionInput) =>
    mutationData<{ reward: RewardDefinition }>(
      await post(`${root}/reward-definitions`, input),
    ),

  updateRewardDefinition: async (
    definitionId: number,
    input: RewardDefinitionInput,
  ) =>
    mutationData<{ approvalRequest: { id: number; status: string } }>(
      await patch(`${root}/reward-definitions/${definitionId}`, input),
    ),

  listMappings: async (filters: {
    mappingStatus?: LifecycleStatus | "";
    market?: string;
  } = {}) =>
    (await get(`${root}/mappings${queryString(filters)}`)) as {
      mappings: RewardMapping[];
    },

  createMapping: async (input: RewardMappingInput) =>
    mutationData<{ mapping: RewardMapping }>(
      await post(`${root}/mappings/create`, input),
    ),

  updateMapping: async (
    mappingId: number,
    input: Partial<Omit<RewardMappingInput, "trigger">>,
  ) =>
    mutationData<{ approvalRequest: { id: number; status: string } }>(
      await patch(`${root}/mappings/${mappingId}`, input),
    ),

  archiveMapping: async (mappingId: number, reason: string) =>
    mutationData<{ approvalRequest: { id: number; status: string } }>(
      await post(`${root}/mappings/${mappingId}/archive`, { reason }),
    ),

  listFulfilmentTasks: async (filters: {
    status?: FulfilmentTask["status"] | "";
    limit?: number;
    offset?: number;
    assignedAdminId?: number;
    overdue?: boolean;
  } = {}) =>
    (await get(
      `${root}/fulfilment-tasks${queryString(filters)}`,
    )) as {
      tasks: FulfilmentTask[];
      total: number;
    },

  completeFulfilmentTask: async (taskId: number, completionNote: string) =>
    mutationData<{ task: FulfilmentTask }>(
      await post(`${root}/fulfilment-tasks/${taskId}/complete`, {
        completionNote,
      }),
    ),

  updateFulfilmentTask: async (
    taskId: number,
    input: {
      status?: "pending" | "in_progress" | "cancelled";
      assignedAdminId?: number | null;
      assignToSelf?: boolean;
      dueAt?: string | null;
    },
  ) =>
    mutationData<{ task: FulfilmentTask }>(
      await patch(`${root}/fulfilment-tasks/${taskId}`, input),
    ),

  listConfigurationHistory: async (filters: {
    entityType?: string;
    entityId?: number;
    limit?: number;
    offset?: number;
  } = {}) =>
    (await get(
      `${root}/configuration-history${queryString(filters)}`,
    )) as {
      versions: ConfigurationVersion[];
      total: number;
    },
};
